/**
 * Home Loan Insert API Service
 * Handles home loan application submission
 *
 * Refactored from: 3in1cms/bajajfinserv-web/ui.solid/src/lib/connectionManager/hlInsertApi.ts
 * Logic preserved AS-IS, using Redux instead of SolidJS signals
 */

import type { IHlInsertApiRequest, IHlInsertApiResponse } from "../types/hlInsertApiTypes";
import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { logPageActionToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import { getLoginState, commonInsertParameters } from "@repo/shared-utils/insert-params";
import { request } from "../core/request";
import { deleteFromCartApi } from "./cart.service";
import { API_RESPONSE_CODES, HL_INSERT_EVENT_CODE, OPERATION_IDS, V4 } from "./constants";

/**
 * Delete item from cart (internal helper)
 * Used before inserting new home loan application
 */
async function deleteFromCart(mobileNo: string, appId: string) {
  try {
    await deleteFromCartApi({ mobileNo, appId, channel: "3in1" });
  } catch (deleteError) {
    logPageActionToNewRelic("HL_DELETE_CART_ERROR", {
      component: "HL Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (deleteError as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!mobileNo,
      }),
    });
  }
}

/**
 * Insert home loan application
 *
 * @param data - Home loan application request data
 * @returns Promise with home loan insert response
 *
 * @example
 * ```typescript
 * const response = await hlInsert({
 *   mobileNo: "9876543210",
 *   appId: "HL123456",
 *   loanAmount: "5000000",
 *   tenure: "240"
 * });
 * ```
 */
export const hlInsert = async (data: IHlInsertApiRequest): Promise<IHlInsertApiResponse> => {
  try {
    // Delete from cart if mobileNo and appId are provided
    if (data.mobileNo && data.appId) {
      await deleteFromCart(data.mobileNo, data.appId);
    }

    const response = (await request({
      operationId: OPERATION_IDS.hlInsert,
      requestData: {
        ...commonInsertParameters(),
        appVersion: V4,
        eventCode: HL_INSERT_EVENT_CODE,
        ...data,
      },
    })) as IHlInsertApiResponse;

    if (response.statusCode !== API_RESPONSE_CODES.STATUS_CODE_7001) {
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error) {
    logPageActionToNewRelic("HL_INSERT_ERROR", {
      component: "HL Insert",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      message: (error as Error).message,
      debugInfo: JSON.stringify({
        loginState: getLoginState(),
        hasAToken: typeof window !== "undefined" && !!localStorage.getItem(COMMON_SSO_ACCESS_TOKEN),
        hasMobile: !!data.mobileNo,
      }),
    });
    throw error;
  }
};
